// Системные уведомления (Web Push) этого устройства: подписка в браузере (PushManager с ключом сервера) и её копия
// на сервере (server/push.go), список устройств аккаунта с уведомлениями для профиля, проверка доставки. Нужны игре
// по переписке: без них в такую партию не войти. Состояние — st.push; после каждого шага — repaint.
// На iPhone Web Push есть только у приложения на экране «Домой» (iOS 16.4+): в Safari-вкладке PushManager нет.
import type { PushDevice } from '../types.ts';
import { clientId, deviceKey } from './outbox.ts';
import { st } from './store.ts';
import type { PushStatus } from './store.ts';

let repaint: () => void = () => {};

/** Кого перерисовывать, когда состояние уведомлений изменилось */
export function onPushChange(cb: () => void): void {
  repaint = cb;
}

const isIos = (): boolean => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/** Что умеет браузер: null — подписаться можно */
function unsupported(): PushStatus | null {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || location.protocol === 'file:') return 'unsupported';
  if (!('PushManager' in window) || typeof Notification === 'undefined') return isIos() ? 'iosBrowser' : 'unsupported';
  return null;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.ready;
}

/** Ключ сервера (base64url) → байты для applicationServerKey */
export function keyBytes(b64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function call(path: string, extra: Record<string, unknown> = {}): Promise<{ devices?: PushDevice[]; sent?: number }> {
  const r = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client: clientId(), key: deviceKey(), ...extra }),
  });
  const j = (await r.json().catch(() => null)) as { error?: string; devices?: PushDevice[]; sent?: number } | null;
  if (!r.ok) throw new Error(j?.error ?? `ошибка сервера (${r.status})`);
  return j ?? {};
}

function setDevices(j: { devices?: PushDevice[] }): void {
  if (Array.isArray(j.devices)) st.push.devices = j.devices;
}

const errText = (e: unknown): string => (e instanceof TypeError ? 'Нет связи с сервером' : e instanceof Error ? e.message : String(e));

async function step(what: () => Promise<void>): Promise<boolean> {
  st.push.busy = true;
  st.push.error = '';
  repaint();
  try {
    await what();
    return true;
  } catch (e) {
    st.push.error = errText(e);
    return false;
  } finally {
    st.push.busy = false;
    repaint();
  }
}

/**
 * При запуске: есть подписка в браузере — освежить её на сервере (адрес мог смениться, сервер мог её удалить),
 * нет — «выключено». Разрешение отозвано в настройках — «запрещено».
 */
export async function initPush(): Promise<void> {
  const no = unsupported();
  if (no) {
    st.push.status = no;
    repaint();
    return;
  }
  if (Notification.permission === 'denied') {
    st.push.status = 'denied';
    repaint();
    return;
  }
  try {
    const sub = await (await registration()).pushManager.getSubscription();
    if (!sub) {
      st.push.status = 'off';
    } else {
      st.push.status = 'on';
      setDevices(await call('api/push/subscribe', { sub: sub.toJSON() }));
    }
  } catch {
    /* нет сети — статус по браузеру; сервер узнает при следующем запуске */
    if (st.push.status === 'unknown') st.push.status = 'off';
  }
  repaint();
}

/** Включить уведомления на этом устройстве (кнопка — браузер спрашивает разрешение только по жесту пользователя) */
export async function enablePush(): Promise<boolean> {
  if (unsupported()) return false;
  return step(async () => {
    const perm = await Notification.requestPermission();
    // запрет объясняет сама панель (как его снять), здесь — только отказ в этот раз
    if (perm === 'denied') {
      st.push.status = 'denied';
      throw new Error('');
    }
    if (perm !== 'granted') throw new Error('Без разрешения на уведомления партия тебя не дозовётся');
    const r = await fetch('api/push/key');
    const j = (await r.json().catch(() => null)) as { key?: string; error?: string } | null;
    if (!r.ok || !j?.key) throw new Error(j?.error ?? 'сервер не выдал ключ уведомлений');
    const reg = await registration();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(j.key) });
    setDevices(await call('api/push/subscribe', { sub: sub.toJSON() }));
    st.push.status = 'on';
  });
}

/** Выключить уведомления на этом устройстве */
export async function disablePush(): Promise<void> {
  if (unsupported()) return;
  await step(async () => {
    const sub = await (await registration()).pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      setDevices(await call('api/push/unsubscribe', { endpoint }));
    }
    st.push.status = 'off';
  });
}

/** Устройства аккаунта с уведомлениями (профиль) */
export async function loadPushDevices(): Promise<void> {
  await step(async () => setDevices(await call('api/push/devices')));
}

/** Выключить уведомления на другом устройстве аккаунта (своё — через disablePush, чтобы отписался и браузер) */
export async function removePushDevice(id: string): Promise<void> {
  const mine = st.push.devices?.find((d) => d.id === id)?.me;
  if (mine) return disablePush();
  await step(async () => setDevices(await call('api/push/remove', { id })));
}

/** Проверка: сервер шлёт уведомление на это устройство */
export async function testPush(): Promise<number> {
  let sent = 0;
  await step(async () => {
    sent = (await call('api/push/test')).sent ?? 0;
    if (!sent) throw new Error('Сервер не смог доставить уведомление — выключи и включи уведомления заново');
  });
  return sent;
}
